// Copyright ©️ 2026 eVolve MEP, LLC

import type { BendType, BendTypeInput, ConduitSize, ConduitType } from '@evolvemep/api-ts';

import { convertInchesToImperialString } from 'helpers/convertInchesToImperialString';
import { isNil, isNotNil } from 'helpers/isNotNil';
import type { BendCreatorFormValues } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';

type ConduitLists = {
  conduitTypes: ConduitType[] | undefined;
  conduitSizes: ConduitSize[] | undefined;
};

/**
 * Formats one bend dimension for display.
 * @param value An angle in degrees, or a length in decimal feet.
 */
export const formatBendDimension = ({ isAngle }: Pick<BendTypeInput, 'isAngle'>, value: number) =>
  // Validation can return float noise such as 29.999999999999996, so angles show at most two decimal places.
  isAngle ? `${Number(value.toFixed(2))}°` : convertInchesToImperialString(value * 12);

/** Conduit type names too long for a Material pill, by the name the Shop API returns. */
const SHORT_CONDUIT_TYPE_NAMES: Partial<Record<string, string>> = { AluminumEMT: 'Alum. EMT' };

/** The conduit type's name as a Material pill shows it. */
export const formatConduitTypePill = (name: string) => SHORT_CONDUIT_TYPE_NAMES[name] ?? name;

/** Formats the conduit as `3/4" EMT`, or null until both conduit type and size are chosen. */
export const formatConduit = (
  { conduitTypeId, conduitSizeId }: Pick<BendCreatorFormValues, 'conduitTypeId' | 'conduitSizeId'>,
  { conduitTypes, conduitSizes }: ConduitLists,
) => {
  const typeName = conduitTypes?.find((type) => type.conduitTypeId === conduitTypeId)?.name;
  const sizeName = conduitSizes?.find((size) => size.conduitSizeId === conduitSizeId)?.name;
  if (isNil(typeName) || isNil(sizeName)) return null;
  return `${sizeName} ${typeName}`;
};

/** Formats the material as `3/4" EMT · 10'`, or null until conduit type, size and stock length are all chosen. */
export const formatMaterial = (values: BendCreatorFormValues, conduitLists: ConduitLists) => {
  const conduit = formatConduit(values, conduitLists);
  if (isNil(conduit) || isNil(values.materialStockLength)) return null;
  return `${conduit} · ${String(values.materialStockLength)}'`;
};

/** The bend type's inputs that have a value, in the bend type's order. */
export const getEnteredDimensions = (values: BendCreatorFormValues, bendType: BendType | null | undefined) =>
  (bendType?.inputs ?? []).flatMap((input) => {
    const value = values[input.keyName];
    return isNotNil(value) ? [{ input, value }] : [];
  });

/** Formats the required dimensions as `30° · 1'-2"`, with ` · centered` for Center Bend, or null when none are entered. */
export const formatDimensionsSummary = (values: BendCreatorFormValues, bendType: BendType | null | undefined) => {
  const required = getEnteredDimensions(values, bendType).filter(({ input }) => input.isRequired);
  if (required.length === 0) return null;
  return [
    ...required.map(({ input, value }) => formatBendDimension(input, value)),
    values.centerBend ? 'centered' : null,
  ]
    .filter(isNotNil)
    .join(' · ');
};

/** One line describing a whole bend, such as `Kick 90 Right · 3/4" EMT · 10' · Angle 30° · Stub Height 1'-2"`. */
export const formatBendDetails = (
  values: BendCreatorFormValues,
  bendType: BendType | null | undefined,
  conduitLists: ConduitLists,
) =>
  [
    values.bendType,
    formatMaterial(values, conduitLists),
    ...getEnteredDimensions(values, bendType).map(
      ({ input, value }) => `${input.name} ${formatBendDimension(input, value)}`,
    ),
    values.centerBend && isNotNil(values.materialStockLength)
      ? `Centered on ${String(values.materialStockLength)}'`
      : null,
  ]
    .filter(isNotNil)
    .join(' · ');
